import Script from 'next/script'
import React, { forwardRef, useEffect, useId, useState } from 'react'
import { Alert, Box, InputAdornment, InputBaseComponentProps, Stack, TextField, useEventCallback } from '@mui/material'
import { PAY_JP_KEY } from '~/lib/config'
import { CardBrandIcon } from '~/components/common/CardBrandIcon'
import { CardBrand } from '~/lib/types/AdvAccount'
import { addCard, createCard } from '~/api/PaymentRepository'

import { LoadingButton } from '@mui/lab'
import { motion, AnimatePresence, useAnimation } from 'framer-motion'
import { UserAdv } from '~/lib/types/UserAdv'
import { formatPhoneNumberE164 } from '~/lib/phoneNumber'
import { logger } from '~/lib/logger'

/* eslint-disable no-var */
declare global {
  var Payjp: any
  var payjp: any
}

const PayjpCardRegister = (props: {
  action: 'add' | 'create'
  userAdv: UserAdv
  onRegistered?: () => void
}) => {
  const [ready, setReady] = useState(typeof payjp !== 'undefined')
  const [elements, setElements] = useState<any>()
  const [createToken, setCreateToken] = useState<any>()

  const handleScriptReady = () => {
    if (!window.payjp) {
      window.payjp = window.Payjp(PAY_JP_KEY)
      setReady(true)
    }
  }

  useEffect(() => {
    if (!ready || elements) return

    const _elements = window.payjp.elements()

    setElements(_elements)

    setCreateToken(() => {
      return (options: any) => window.payjp.createToken(_elements.getElement('cardNumber'), options)
    })
  }, [ready, elements])

  const handleSubmit = async () => {
    if (formState.pending) return

    resetState()

    const cardAction = async ({ token, action }: { token: string; action: string }) => {
      switch (action) {
        case 'add': {
          await addCard(token)
            .then(() => props.onRegistered?.())
            .catch((e) => {
              handleFormError('カードの登録に失敗しました')
              logger.error(new Error('Failed to add card', { cause: e }))
            })
          break
        }
        case 'create': {
          await createCard(token)
            .then(() => props.onRegistered?.())
            .catch((e) => {
              handleFormError('カードの登録に失敗しました')
              logger.error(new Error('Failed to create card', { cause: e }))
            })
          break
        }
      }
    }

    const run = async (callback: () => Promise<void>) => {
      let timer: ReturnType<typeof setTimeout> | undefined
      let promise: Promise<void> | undefined

      try {
        timer = setTimeout(() => {
          setFormState((prev) => ({ ...prev, pending: true }))
          promise = new Promise<void>((r) => setTimeout(() => r(), 500))
        }, 100)

        await callback()
      } finally {
        clearTimeout(timer)
        await promise
        setFormState((prev) => ({ ...prev, pending: false }))
      }
    }

    run(async () => {
      let result

      const phone = (props.userAdv.tel && formatPhoneNumberE164(props.userAdv.tel)) ?? undefined
      result = await createToken({
        three_d_secure: true,
        card: { name: cardName, email: cardEmail, phone },
      }).catch((e: unknown) => ({ error: e }))
      if (result.error?.param === 'card[phone]') {
        logger.error(new Error(`PayJP: Invalid phone number: original=${props.userAdv.tel} formatted=${phone}`))
        result = await createToken({
          three_d_secure: true,
          card: { name: cardName, email: cardEmail /* phone: undefined */ },
        }).catch((e: unknown) => ({ error: e }))
      }

      if (result.error) {
        logger.error(
          `PayJP: createToken error: param=${result.error.param} code=${result.error.code}: ${result.error.message}`,
        )
        handleFormError(result.error.message, result.error.param)
      } else {
        await cardAction({ token: result.id, action: props.action })
      }
    })
  }

  const handleFormError = async (error: string, field?: string) => {
    if (field === 'card[email]') {
      setCardEmailError(error)
      updateFormState({ emailFieldShown: true })
    } else {
      setFormState((prev) => ({ ...prev, formError: error }))
    }

    setFormState((prev) => ({ ...prev, buttonError: true }))
    await buttonAnimationControls.start({
      x: [0, -10, 10, -10, 10, 0],
      transition: { duration: 0.5 },
    })
    setFormState((prev) => ({ ...prev, buttonError: false }))
  }

  const [cardName, setCardName] = useState('')
  const [cardEmail, setCardEmail] = useState(props.userAdv.email)

  const [cardBrand, setCardBrand] = useState<CardBrand | null>(null)

  const [formState, setFormState] = useState({
    pending: false,
    formError: '',
    buttonError: false,
    emailFieldShown: false,
  })
  const updateFormState = (state: Partial<typeof formState>) => setFormState((prev) => ({ ...prev, ...state }))

  const [cardNumberError, setCardNumberError] = useState('')
  const [cardExpiryError, setCardExpiryError] = useState('')
  const [cardCvcError, setCardCvcError] = useState('')
  const cardNameError = cardName && !/^[\w\d\s.-]{0,45}$/.test(cardName) ? '形式が正しくありません。' : ''
  const [cardEmailError, setCardEmailError] = useState('')

  const resetState = () => {
    updateFormState({ formError: '', buttonError: false })
    setCardNumberError('')
    setCardExpiryError('')
    setCardCvcError('')
    setCardEmailError('')
  }

  const buttonAnimationControls = useAnimation()

  return (
    <Stack gap={3}>
      <Script src="https://js.pay.jp/v2/pay.js" onReady={handleScriptReady} />

      <Stack gap={2}>
        <TextField
          label="カード番号"
          error={!!cardNumberError}
          helperText={cardNumberError}
          InputProps={{
            inputComponent: InputProxy,
            inputProps: {
              elements,
              type: 'cardNumber',
              onElementChange: (e: any) => {
                setCardNumberError(e.error?.message ?? '')
                const brand = e.brand as 'Visa' | 'Mastercard' | 'JCB' | 'American Express' | 'Diners Club' | 'Discover'
                setCardBrand(brand === 'Mastercard' ? 'MasterCard' : brand)
              },
            },
            endAdornment: cardBrand && (
              <InputAdornment position="end">
                <CardBrandIcon brand={cardBrand} size={26} />
              </InputAdornment>
            ),
          }}
        />

        <Stack direction="row" gap={2} flexWrap={['wrap', 'initial']}>
          <TextField
            label="有効期限 (月/年)"
            fullWidth
            error={!!cardExpiryError}
            helperText={cardExpiryError}
            InputProps={{
              inputComponent: InputProxy,
              inputProps: {
                elements,
                type: 'cardExpiry',
                onElementChange: (e: any) => setCardExpiryError(e.error?.message ?? ''),
              },
            }}
          />
          <TextField
            label="セキュリティコード"
            fullWidth
            error={!!cardCvcError}
            helperText={cardCvcError}
            InputProps={{
              inputComponent: InputProxy,
              inputProps: {
                elements,
                type: 'cardCvc',
                onElementChange: (e: any) => setCardCvcError(e.error?.message ?? ''),
              },
            }}
          />
        </Stack>

        <TextField
          label="カード名義"
          error={!!cardNameError}
          helperText={cardNameError}
          value={cardName}
          onChange={(e) => setCardName(e.target.value)}
        />

        <AnimatePresence>
          {formState.emailFieldShown && (
            <Box component={motion.div} initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}>
              <TextField
                fullWidth
                label="メールアドレス"
                error={!!cardEmailError}
                helperText={cardEmailError}
                value={cardEmail}
                onChange={(e) => setCardEmail(e.target.value)}
              />
            </Box>
          )}
        </AnimatePresence>
      </Stack>

      <Stack direction="column">
        <AnimatePresence>
          {formState.formError && (
            <Box
              mt={-1}
              mb={2}
              component={motion.div}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
            >
              <Alert severity="error">{formState.formError}</Alert>
            </Box>
          )}
        </AnimatePresence>

        <Stack component={motion.div} animate={buttonAnimationControls}>
          <LoadingButton
            variant="contained"
            loading={formState.pending}
            color={formState.buttonError ? 'error' : 'primary'}
            onClick={handleSubmit}
          >
            カードを登録
          </LoadingButton>
        </Stack>
      </Stack>
    </Stack>
  )
}

export default PayjpCardRegister

const setRef = <T,>(ref: React.ForwardedRef<T>, value: T) => {
  if (typeof ref === 'function') {
    ref(value)
  } else if (ref) {
    ref.current = value
  }
}

const InputProxy = forwardRef<HTMLInputElement, InputBaseComponentProps>(
  ({ elements, type, onElementChange, ...props }, ref) => {
    const id = useId()

    const [element, setElement] = useState<any>()
    const elementRef = React.useRef<any>(null)
    elementRef.current = element

    useEffect(() => {
      if (!elements || elementRef.current) return

      const element = elements.create(type, { placeholder: '', style: { base: {}, invalid: { color: 'initial' } } })
      element.mount(`#${CSS.escape(id)}`)
      setElement(element)
    }, [elements, id, type])

    const handleFocus = useEventCallback(() => {
      const event = createSyntheticEvent<HTMLInputElement, FocusEvent>(new FocusEvent('focus'))
      props.onFocus?.({
        ...event,
        target: event.target as EventTarget & HTMLInputElement,
        relatedTarget: null,
      })
    })

    const handleBlur = useEventCallback(() => {
      const event = createSyntheticEvent<HTMLInputElement, FocusEvent>(new FocusEvent('blur'))
      props.onBlur?.({
        ...event,
        target: event.target as EventTarget & HTMLInputElement,
        relatedTarget: null,
      })
    })

    const handleChange = useEventCallback((e: any) => {
      const event = new Event('change')
      const value = e.empty ? '' : 'has opaque value'
      Object.defineProperty(event, 'target', { writable: false, value: { value } })
      props.onChange?.(createSyntheticEvent(event))

      onElementChange?.(e)
    })

    useEffect(() => {
      if (!element) return

      const input = document.createElement('input')
      setRef(ref, input)

      element.on('focus', () => {
        handleFocus()
      })

      element.on('blur', () => {
        handleBlur()
      })

      element.on('change', (e: any) => {
        handleChange(e)
      })
    }, [element, handleBlur, handleChange, handleFocus, ref])

    return <Box className={props.className} onClick={() => element?.focus()} id={id} />
  },
)
InputProxy.displayName = 'InputProxy'

const createSyntheticEvent = <T extends Element, E extends Event>(event: E): React.SyntheticEvent<T, E> => {
  let isDefaultPrevented = false
  let isPropagationStopped = false
  const preventDefault = () => {
    isDefaultPrevented = true
    event.preventDefault()
  }
  const stopPropagation = () => {
    isPropagationStopped = true
    event.stopPropagation()
  }
  return {
    nativeEvent: event,
    currentTarget: event.currentTarget as EventTarget & T,
    target: event.target as EventTarget & T,
    bubbles: event.bubbles,
    cancelable: event.cancelable,
    defaultPrevented: event.defaultPrevented,
    eventPhase: event.eventPhase,
    isTrusted: event.isTrusted,
    preventDefault,
    isDefaultPrevented: () => isDefaultPrevented,
    stopPropagation,
    isPropagationStopped: () => isPropagationStopped,
    persist: () => {},
    timeStamp: event.timeStamp,
    type: event.type,
  }
}
